import { useState } from "react";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import { PricingCalculator } from "@/components/PricingCalculator";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Package, TrendingUp, Zap } from "lucide-react";
const Pricing = () => {
  const {
    language,
    setLanguage,
    content: currentContent
  } = useLanguage();
  return <div className="min-h-screen bg-background">
      <Header language={language} onLanguageChange={setLanguage} content={currentContent.header} />
      
      <main className="pt-24">
        {/* Price Table Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-primary bg-clip-text text-transparent">Prislista för enskilda filmer</h2>
              
              {/* Bullet List with Checkmarks */}
              <div className="mb-12 bg-muted/30 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Fasta priser – allt ingår:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>Idé & manus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>AI-generering/manuell distansproduktion</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>Integrera med ert eget bild- eller filmmaterial</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>Redigering & postproduktion</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="font-semibold">Leverans av färdig film – inga dolda kostnader</span>
                  </li>
                </ul>
              </div>
              
              <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
                <CardContent className="p-0">
                  <div className="overflow-hidden rounded-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border/50 bg-muted/30">
                          <th className="text-left p-6 font-bold text-lg">Längd på film</th>
                          <th className="text-right p-6 font-bold text-lg">Kostnad</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="p-6 text-lg">15 sekunder</td>
                          <td className="text-right p-6 text-lg font-semibold">4 500 kr</td>
                        </tr>
                        <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="p-6 text-lg">30 sekunder</td>
                          <td className="text-right p-6 text-lg font-semibold">9 000 kr</td>
                        </tr>
                        <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="p-6 text-lg">60 sekunder</td>
                          <td className="text-right p-6 text-lg font-semibold">18 000 kr</td>
                        </tr>
                        <tr className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                          <td className="p-6 text-lg">90 sekunder</td>
                          <td className="text-right p-6 text-lg font-semibold">25 000 kr</td>
                        </tr>
                        <tr className="hover:bg-muted/20 transition-colors">
                          <td className="p-6 text-lg">120+ sekunder</td>
                          <td className="text-right p-6 text-lg font-semibold text-primary">Offert</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              
              <p className="text-center text-muted-foreground mt-6 text-sm">
                Alla priser är exklusive moms
              </p>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-24 bg-gray-100 dark:bg-gray-800/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent" style={{
              lineHeight: '1.4em'
            }}>Flexibelt med credits</h1>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="bg-card/80 backdrop-blur-sm shadow-lg border-0">
                <CardContent className="p-8 space-y-6">
                  {/* How it Works */}
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-center">Så här fungerar det</h2>
                    
                    {/* Pricing Rules */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                        <span className="font-semibold">Nya filmer</span>
                        <Badge>1 credit/sekund</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                        <span className="font-semibold">Kortversioner</span>
                        <Badge variant="secondary">0.5 credit/sekund</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                        <span className="font-semibold">Ny formatversion</span>
                        <Badge variant="secondary">10 credits/film</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                        <span className="font-semibold">Ny språkversion</span>
                        <Badge variant="secondary">10 credits/film</Badge>
                      </div>
                    </div>

                    {/* Key Benefits */}
                    <div className="grid grid-cols-3 gap-4 pt-4">
                      <div className="text-center">
                        <Check className="w-5 h-5 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">Ingen tidsgräns</p>
                      </div>
                      <div className="text-center">
                        <Check className="w-5 h-5 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">Total flexibilitet</p>
                      </div>
                      <div className="text-center">
                        <Check className="w-5 h-5 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">Bättre värde</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Credit Packages Section */}
        <section className="py-24 bg-gray-50 dark:bg-gray-900/20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 bg-gradient-primary bg-clip-text text-transparent">
              Föreslagna paket
            </h2>
            <p className="text-center text-muted-foreground mb-12">1 credit = 1 sekund färdig film</p>
            
            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {currentContent.pricing.creditPackages.map((pkg, index) => {
              const iconMap = {
                package: Package,
                "trending-up": TrendingUp,
                zap: Zap
              };
              const Icon = iconMap[pkg.icon as keyof typeof iconMap];
              return <Card key={index} className="group hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border-0 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm shadow-md">
                    <CardHeader className="text-center pb-6">
                      <div className="w-20 h-20 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <CardTitle className="text-4xl font-bold mb-3 bg-gradient-primary bg-clip-text text-transparent">{pkg.name}</CardTitle>
                      <div className="space-y-2">
                        <div className="text-3xl font-bold">{pkg.credits} {language === "sv" ? "credits" : "credits"}</div>
                        <div className="text-2xl font-bold text-primary">{pkg.price} SEK</div>
                        
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="text-center">
                         <Badge variant="secondary" className="text-sm px-3 py-1">
                           {pkg.pricePerCredit} / credit
                         </Badge>
                      </div>
                      
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Usage Examples */}
            <div className="max-w-4xl mx-auto mt-16">
              <h2 className="text-3xl font-bold text-center mb-12">Så här kan du använda dina credits</h2>
              
              <div className="space-y-8">
                {/* Exempel 1 */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-2xl">Exempel 1</CardTitle>
                    <CardDescription className="text-lg">
                      Ett smidigt paket för att snabbt få ut en film på hemsidan och i sociala medier, med möjlighet att nå en internationell publik.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">1</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">Huvudfilm (30s)</p>
                          <p className="text-sm text-muted-foreground">30s × 1 = 30 credits</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">2</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">Kortversion (15s & 6s)</p>
                          <p className="text-sm text-muted-foreground">21s × 0.5 = 10.5 credits</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">3</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">Extra språk på huvudfilmen (EN & GER)</p>
                          <p className="text-sm text-muted-foreground">1 film × 20 = 20 credits</p>
                        </div>
                      </li>
                    </ul>
                    <div className="pt-4 border-t border-border/50 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Total kostnad:</span>
                        <span className="text-2xl font-bold text-primary">60 credits</span>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="text-sm"><span className="font-semibold">Leverans:</span> En huvudfilm (på två språk) och två kortversioner (15s och 6s).</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Exempel 2 */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-2xl">Exempel 2</CardTitle>
                    <CardDescription className="text-lg">
                      Tre helt nya, unika filmer för en kampanj där du vill testa olika budskap och visuella idéer.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">1</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">3 st nya, unika filmer (30s)</p>
                          <p className="text-sm text-muted-foreground">30s × 1 credit/s × 3 filmer = 90 credits</p>
                          <p className="text-xs text-muted-foreground italic mt-1">(Här gäller ordinarie sekundpris eftersom varje film är en ny, komplett produktion)</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">2</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">Formatversion (9:16)</p>
                          <p className="text-sm text-muted-foreground">3 × 10 credits = 30 credits</p>
                        </div>
                      </li>
                    </ul>
                    <div className="pt-4 border-t border-border/50 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Total kostnad:</span>
                        <span className="text-2xl font-bold text-primary">120 credits</span>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="text-sm"><span className="font-semibold">Leverans:</span> 3 helt olika videor i 2 format, redo för kampanjstart. Totalt 6 videofiler.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Exempel 3 */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-2xl">Exempel 3</CardTitle>
                    <CardDescription className="text-lg">
                      Komplett filmpaket med flera format och språk för global räckvidd.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">1</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">2st nya huvudfilmer (90s + 60s)</p>
                          <p className="text-sm text-muted-foreground">150s × 1 = 150 credits</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">2</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">4 st kortversioner från dessa (4×15s)</p>
                          <p className="text-sm text-muted-foreground">60s × 0.5 = 30 credits</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">3</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">4 st formatversioner (9:16)</p>
                          <p className="text-sm text-muted-foreground">4 filmer × 10 = 40 credits</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Badge variant="secondary" className="mt-1 shrink-0">4</Badge>
                        <div className="flex-1">
                          <p className="font-semibold">4 st extra språk på huvudfilmerna</p>
                          <p className="text-sm text-muted-foreground">2 filmer × 40 = 80 credits</p>
                        </div>
                      </li>
                    </ul>
                    <div className="pt-4 border-t border-border/50 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Total kostnad:</span>
                        <span className="text-2xl font-bold text-primary">300 credits</span>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="text-sm"><span className="font-semibold">Leverans:</span> 8 unika videofiler (olika längd/format), alla levererade på 2 språk. Totalt 16 videofiler.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

          </div>
        </section>

        {/* Pricing Calculator Section */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <PricingCalculator />
          </div>
        </section>

      </main>

      <ContactSection content={currentContent.contact} />
      
      <Footer content={{
      company: "Volym Digitalbyrå AB",
      rights: language === "sv" ? "Alla rättigheter förbehållna." : "All rights reserved."
    }} />
    </div>;
};
export default Pricing;